// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * bot.ts — the AI pit wall. Used two ways: as the solo opponent field, and as
 * the referee in the balance sim (principle #18). A bot only ever sees what a
 * human sees — `Game.view()` (noisy, sometimes-offline telemetry + the radio) —
 * never the true severity.
 *
 * The strategies exist to answer the idea's two gates directly:
 *   - `full` services everything every stop (the "raw tapping, do it all" bot);
 *     `tele`/`radio` triage. If triage does not beat `full`, the game is a twitch
 *     test with a story painted on it.
 *   - `radio` weighs the driver's line against telemetry; `tele` ignores it. If
 *     the radio does not make `radio` beat `tele`, the fiction is decorative.
 *
 * A good triage bot is not a threshold on the raw reading — a real engineer
 * carries a PREDICTION of the car's state (fuel burns ~0.4 a stint whether or not
 * the gauge is readable) and fuses the sensor into it. That is what makes the
 * radio worth anything: when the sensor is dark, the prediction is all `tele`
 * has, and the prediction cannot foresee an INCIDENT (a wall-brush, a locked
 * brake). The radio can. So `radio`'s entire edge is catching the spike the
 * prediction and the dead sensor both miss — which is exactly why it shows up in
 * Monsoon and barely in Sprint.
 */

import {
  CATA_THRESH,
  JOB_TIME,
  PENALTY_K,
  SYSTEMS,
  type Game,
  type StopView,
  type System,
} from './game';
import type { Mode } from './modes';
import { Game as GameClass } from './game';
import { makeRng, type Rng } from '@ben-gy/game-engine/rng';

export type Strategy = 'full' | 'min' | 'tele' | 'radio' | 'optimal';

/** Break-even severity: below it, the stint penalty is cheaper than the job. */
function breakEven(s: System): number {
  return Math.sqrt(JOB_TIME[s] / PENALTY_K[s]);
}

/** Expected base wear the engineer can predict (incidents are unpredictable). */
function expectedWear(s: System, mode: Mode): number {
  const BASE: Record<System, number> = { tyres: 0.26, fuel: 0.42, wing: 0.06, brakes: 0.14 };
  return BASE[s] * mode.wearScale;
}

const PRIOR: Record<System, number> = { tyres: 0.34, fuel: 0.44, wing: 0.06, brakes: 0.22 };

export interface BotConfig {
  strategy: Strategy;
  /** Added to every fix threshold — a lazier (higher) or keener (lower) engineer. */
  thresholdBias?: number;
  /** Seconds of execution slack per stop, for a solo-field opponent's realism. */
  execSlack?: number;
  rng?: Rng;
}

/**
 * A stateful triage engineer. Persist one across a whole race so its prediction
 * of the car carries between stops.
 */
export class Bot {
  private pred: Record<System, number>;
  private readonly cfg: Required<Omit<BotConfig, 'rng'>>;
  private readonly rng: Rng | null;

  constructor(
    private readonly mode: Mode,
    cfg: BotConfig,
  ) {
    this.pred = { ...PRIOR };
    this.cfg = {
      strategy: cfg.strategy,
      thresholdBias: cfg.thresholdBias ?? 0,
      execSlack: cfg.execSlack ?? 0,
    };
    this.rng = cfg.rng ?? null;
  }

  /** Choose the systems to service at this stop. */
  decide(view: StopView): Set<System> {
    const fix = new Set<System>();
    if (this.cfg.strategy === 'min') {
      // Fix nothing — but never knowingly send it out to a certain catastrophe,
      // or "min" just measures blow-ups rather than being a real lower bound.
      for (const t of view.telemetry) {
        const e = t.reading ?? this.pred[t.system];
        if (e >= CATA_THRESH[t.system]) fix.add(t.system);
      }
      return fix;
    }
    if (this.cfg.strategy === 'full') {
      for (const s of SYSTEMS) fix.add(s);
      return fix;
    }

    const useRadio = this.cfg.strategy === 'radio';
    for (const t of view.telemetry) {
      const s = t.system;
      // Fuse the sensor into the carried prediction; fall back to prediction when dark.
      let est = t.reading === null ? this.pred[s] : 0.32 * this.pred[s] + 0.68 * t.reading;
      if (useRadio && view.radio.alarm && view.radio.system === s) {
        // The driver is shouting about THIS system. Weigh it against the DATA:
        // when the sensor is live and reliable, a live reading outranks a
        // subjective (and ~1-in-4 wrong) driver, so the radio only nudges a
        // borderline call — it must not override a gauge that plainly reads OK.
        // When the sensor is DARK, the radio is the only evidence there is, so
        // it carries real weight. That asymmetry is the whole point of the
        // mechanic and is exactly why it pays off in Monsoon and barely in Sprint.
        const conf = t.reading === null ? 0.66 : 0.12;
        est = est + conf * (1 - est);
      }
      const theta = breakEven(s) + this.cfg.thresholdBias - 0.08; // escalation-aware
      const cataGuard = CATA_THRESH[s] - 0.14;
      if (est >= theta || est >= cataGuard) fix.add(s);
      this.pred[s] = fix.has(s) ? 0 : est;
    }
    return fix;
  }

  /** After committing, roll the prediction forward by the wear we can foresee. */
  advance(): void {
    for (const s of SYSTEMS) this.pred[s] = clamp01(this.pred[s] + expectedWear(s, this.mode));
  }

  execSlack(): number {
    if (this.cfg.execSlack <= 0) return 0;
    const r = this.rng ? this.rng() : 0.5;
    return this.cfg.execSlack * r;
  }
}

export interface BotResult {
  total: number;
  catastrophes: number;
  fixes: number;
}

/** Play a whole race with one bot and return its result. */
export function botPlay(seed: number, mode: Mode, cfg: BotConfig): BotResult {
  const g: Game = new GameClass({ seed, mode });
  const bot = new Bot(mode, cfg);
  let fixes = 0;
  while (!g.over) {
    const fix = bot.decide(g.view());
    fixes += fix.size;
    g.commit(fix, bot.execSlack());
    bot.advance();
  }
  const s = g.summary();
  return { total: s.total, catastrophes: s.catastrophes, fixes };
}

/** A field of solo opponents at graded skill, deterministic from the seed. */
export function opponentField(
  seed: number,
  mode: Mode,
  names: readonly string[],
): Array<{ name: string; total: number; catastrophes: number }> {
  const rng = makeRng(seed ^ 0x9e3779b9);
  // Skill tiers: keener engineers make sharper calls and cleaner stops.
  const tiers: Array<{ bias: number; slack: number }> = [
    { bias: -0.02, slack: 0.3 },
    { bias: 0.0, slack: 0.6 },
    { bias: 0.04, slack: 0.9 },
    { bias: 0.09, slack: 1.3 },
    { bias: 0.15, slack: 1.8 },
  ];
  return names.map((name, i) => {
    const tier = tiers[i % tiers.length];
    const r = botPlay(seed + i * 101, mode, {
      strategy: 'radio',
      thresholdBias: tier.bias,
      execSlack: tier.slack,
      rng: makeRng((seed ^ 0x51ed270b) + i),
    });
    void rng;
    return { name, total: r.total, catastrophes: r.catastrophes };
  });
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
